// Literal Encompass field IDs used by the Current Loan Dashboard. Every ID here is the SAME
// one the Add New Loan wizard (src/components/loan-form/*) already reads/writes, so the
// Dashboard saves to Encompass exactly the way the wizard does:
//   - Step 1  create-loan-application / edit-loan-application (property units, LO/LP, foreign national)
//   - Step 2  loan-address-application (credit reissue)
//   - Step 10 create-loan-fee (closing information)
//
// Do NOT add an ID here unless it's been confirmed against a real, working save/read call
// elsewhere in the codebase — guessing an Encompass field ID risks silently corrupting a
// real loan record.

// ── Step 10: Closing information (create-loan-fee/index.jsx:206-291) ─────────────────────
// Fee amounts are written to BOTH ids in each pair, same as the wizard.
export const CLOSING_FIELD_IDS = {
	closingDate: "2966",
	purchaseContractDate: "763",
	creditReportFee: "640",
	creditReportProvider: "624",
	appraisalFee: "641",
	appraisalProvider: "617",
	secondAppraisalFee: "NEWHUD.X138",
	secondAppraisalProvider: "NEWHUD.X1052",
	processingFee: "NEWHUD.X136",
	processingProvider: "NEWHUD.X1050",
	thirdPartyFee: "NEWHUD.X1285",
	thirdPartyProvider: "NEWHUD.X1284",
	bpcPercent: "389",
	bpcDollars: "1620",
	escrowCompanyName: "610",
	escrowStreet: "612",
	escrowCity: "613",
	escrowState: "1175",
	escrowZip: "614",
	escrowOfficerName: "611",
	escrowOfficerPhone: "615",
	escrowOfficerEmail: "87",
	poaBorrower: "CX.CPL.USINGPOA.BORROWER",
	poaCoBorrower: "CX.CPL.USINGPOA.COBORROWER",
	cashOutBusinessPurpose: "CX.CPL.BUSPURPOSECASHOUT",
	occupiedByFamily: "CX.CPL.OCCUPIEDBY",
};

// Second id each fee amount is mirrored to (create-loan-fee/index.jsx:223-227).
export const CLOSING_FEE_MIRROR_FIELD_IDS = {
	creditReportFee: "NEWHUD2.X1139",
	appraisalFee: "NEWHUD2.X1106",
	secondAppraisalFee: "NEWHUD2.X1304",
	processingFee: "NEWHUD2.X1238",
	thirdPartyFee: "NEWHUD2.X875",
};

// Fee description written only when that fee has an amount (create-loan-fee/index.jsx:245-255).
export const CLOSING_FEE_DESCRIPTION_FIELDS = {
	secondAppraisalFee: { id: "NEWHUD.X128", value: "2nd Appraisal Fee" },
	processingFee: { id: "NEWHUD.X126", value: "Processing Fee" },
	thirdPartyFee: { id: "NEWHUD.X1283", value: "ThirdPartyProcessingFee" },
};

// Always sent with the closing save (create-loan-fee/index.jsx:238-241).
export const CLOSING_FEE_PAID_TO_FIELD = { id: "NEWHUD.X1287", value: "Broker" };

// Business-purpose answers are written with lock:true (create-loan-fee/index.jsx:242-243).
export const CLOSING_LOCKED_FIELD_KEYS = new Set(["cashOutBusinessPurpose", "occupiedByFamily"]);

export const CLOSING_VESTING_FIELD_IDS = {
	individual: { vestingType: "33", vestingWrittenAs: "CX.PORTAL.FINAL.VESTING" },
	entity: { vestingType: "CX.PORTAL.CORPTRUSTORGTYP", vestingWrittenAs: "CX.PORTAL.CORPTRUSTNAME" },
};
// create-loan-fee writes the entity name as CORPTRUSTNAME but reads CorpTrustName; both are
// requested so either casing is picked up.
export const CLOSING_ENTITY_NAME_READ_ALT_ID = "CX.PORTAL.CorpTrustName";

// vestingMethodOptions (select-options.js) split the same way create-loan-fee does.
export const INDIVIDUAL_VESTING_METHODS = new Set([
	"Sole Ownership",
	"Tenancy in Common",
	"Tenancy by the Entirety",
	"As Joint Tenants With Right of Survivorship",
]);
export const ENTITY_VESTING_METHODS = new Set([
	"A Limited Liability Company",
	"A Corporation",
	"An Inter Vivos Trust",
	"A Trust",
]);

// Encompass-computed / context values the closing section only READS — never written.
export const CLOSING_READ_ONLY_FIELD_IDS = {
	lpcPercent: "NEWHUD.X223",
	lpcDollars: "NEWHUD.X224",
	compensationPlan: "LCP.X1",
	loanOrganization: "TPO.X14",
	occupancy: "1811",
	loanPurpose: "19",
	coBorrowerName: "4004",
};

// ── Step 1: Subject property + Step 2: credit reissue ────────────────────────────────────
export const PROPERTY_FIELD_IDS = {
	// create-loan-application/index.jsx:404-407 — written with property.financedNumberOfUnits
	commercialUnits: "CX.PROPERTY.COMMERCIAL.UNITS",
	// loan-address-application/index.jsx:471-476 — "Manual" / "Reissue"
	creditReissueMethod: "CX.LC.Credit.Provided",
};

// Loan Originator / Loan Processor identity — confirmed in create-loan-application/
// edit-loan-application (TPO.X62/TPO.X75)
export const LO_LP_FIELD_IDS = {
	loanOriginatorId: "TPO.X62",
	loanProcessorId: "TPO.X75",
	// loanOriginatorEmail/loanProcessorEmail/additionalContact: not literal field IDs — read
	// via getLoan()'s response.tPO.lOEmail/lPEmail, and written via the structured
	// `updateLoan({ loanData: { tpo: {...} } })` call, confirmed in edit-loan-application/
	// index.jsx:1215-1339 (see dashboard/index.jsx persistBorrowerData).
};

// Additional loan contact — read back via field() exactly like edit-loan-application:677-688
// (written through updateLoan's `tpo` block for NDC/CORR, see utils/loan-tpo-payload.js).
export const ADDITIONAL_CONTACT_FIELD_IDS = {
	name: "TPO.X61",
	phone: "TPO.X65",
	email: "TPO.X63",
};

/** Builds the {id,value} array for useUpdateFieldsMutation from a flat {fieldKey: value} map
 * and one of the ID dictionaries above. Skips keys with no entry in `idMap`. */
export const toFieldValuesArray = (values, idMap) =>
	Object.entries(values || {})
		.filter(([key]) => idMap[key] !== undefined)
		.map(([key, value]) => ({ id: idMap[key], value: value ?? "" }));
